import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  FaHome,
  FaBoxOpen,
  FaCog,
  FaInfoCircle,
  FaIndustry,
  FaPhoneAlt,
  FaBookOpen,
  FaCloud,
  FaDatabase,
  FaRobot,
  FaCheckCircle,
  FaTools,
  FaServer,
  FaWarehouse,
  FaUserTie,
  FaGraduationCap,
  FaLightbulb,
  FaMobileAlt,
  FaUsers,
  FaBriefcase,
  FaChalkboardTeacher,
  FaStar,
  FaHandshake,
  FaChevronDown,
  FaShieldAlt,
  FaClock,
  FaUserGraduate,
  FaEnvelope,
  FaSun,
  FaBuilding,
  FaPalette,
  FaPlusCircle,
  FaCar,
} from "react-icons/fa";
import "./Navbar.css";
import { RxCross1, RxHamburgerMenu } from "react-icons/rx";
import EmailDisplay from "../components/EmailDisplay";

const Navbar = () => {
  const [showProducts, setShowProducts] = useState(false);
  const [showServices, setShowServices] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth > 1024);

  const navigate = useNavigate();
  const location = useLocation();

  // Helper to close all dropdowns and mobile menu
  const closeDropdowns = () => {
    setShowAbout(false);
    setShowProducts(false);
    setShowServices(false);
    setMenuOpen(false);
  };

  useEffect(() => {
    const handleResize = () => {
      const desktop = window.innerWidth > 1024;
      setIsDesktop(desktop);
      if (desktop) {
        setMenuOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Click outside to close dropdowns (desktop only)
  useEffect(() => {
    if (!isDesktop) return;

    const handleOutsideClick = (e) => {
      const target = e.target;
      if (
        target.closest(".dropdown") ||
        target.closest(".products-dropdown-title")
      ) {
        return;
      }
      closeDropdowns();
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isDesktop]);

  const toggleMenu = () => setMenuOpen(!menuOpen);

  const scrollToSection = (sectionId) => {
    const headerOffset = isDesktop ? 140 : 110;

    const performScroll = (attempt = 0) => {
      const element = document.getElementById(sectionId);
      if (element) {
        const elementTop =
          element.getBoundingClientRect().top + window.scrollY - headerOffset;
        window.scrollTo({
          top: elementTop > 0 ? elementTop : 0,
          behavior: "smooth",
        });
      } else if (attempt < 5) {
        setTimeout(() => performScroll(attempt + 1), 120);
      }
    };

    closeDropdowns(); // Close everything on scroll

    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => performScroll(), 360);
    } else {
      performScroll();
    }
  };

  const highlightProductCard = (productId) => {
    const card = document.getElementById(`product-card-${productId}`);
    if (!card) return;
    card.classList.add("product-card-highlight");
    setTimeout(() => {
      card.classList.remove("product-card-highlight");
    }, 1600);
  };

  const scrollToProductCard = (productId) => {
    scrollToSection(`product-card-${productId}`);
    setTimeout(() => highlightProductCard(productId), 650);
  };

  const highlightServiceCard = (serviceId) => {
    const card = document.getElementById(`service-${serviceId}`);
    if (!card) return;
    card.classList.add("service-card-highlight");
    setTimeout(() => {
      card.classList.remove("service-card-highlight");
    }, 1600);
  };

  const scrollToServiceCard = (serviceId) => {
    scrollToSection(`service-${serviceId}`);
    setTimeout(() => highlightServiceCard(serviceId), 650);
  };

  const handleHomeClick = () => {
    closeDropdowns();
    if (location.pathname !== "/") {
      navigate("/");
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleLogoKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleHomeClick();
    }
  };

  const handleProjectGuidanceClick = () => {
    closeDropdowns();
    if (location.pathname !== "/project-guidance") {
      navigate("/project-guidance");
    }
  };

  const handleEnrollInternshipClick = () => {
    closeDropdowns();
    navigate("/services/internship-training");
  };

  const handleEnrollTrainingClick = () => {
    closeDropdowns();
    if (location.pathname !== "/training") {
      navigate("/training");
    }
  };

  const handleCareersClick = () => {
    closeDropdowns();
    if (location.pathname !== "/careers") {
      navigate("/careers");
    }
  };

  const handleDropdownToggle = (event, dropdown) => {
    if (event.target.closest(".dropdown")) {
      return;
    }

    setShowAbout(dropdown === "about" ? !showAbout : false);
    setShowProducts(dropdown === "products" ? !showProducts : false);
    setShowServices(dropdown === "services" ? !showServices : false);
  };

  return (
    <>
      <div className="top-navbar">
        <div className="top-nav-container">
          <div className="top-nav-items">
            <div className="top-nav-item">
              <FaEnvelope className="top-nav-icon" />
              <EmailDisplay email="careers@pinkmoontech.com" />
            </div>
            {/* <button
              type="button"
              className="top-nav-item"
              onClick={handleEnrollTrainingClick}
            >
              <FaGraduationCap className="top-nav-icon" />
              <span>Register for Training</span>
            </button> */}
            {/* <button
              type="button"
              className="top-nav-item"
              onClick={handleCareersClick}
            >
              <FaUsers className="top-nav-icon" />
              <span>Current Openings</span>
            </button> */}
          </div>
        </div>
      </div>

      <nav className="navbar">
        <div
          className="logo"
          role="button"
          tabIndex={0}
          onClick={handleHomeClick}
          onKeyDown={handleLogoKeyDown}
        >
          <img src="/logo-final.webp" alt="PINKMOON" />
        </div>

        <div className="hamburger" onClick={toggleMenu}>
          {menuOpen ? <RxCross1 /> : <RxHamburgerMenu />}
        </div>

        <ul className={`nav-links ${menuOpen ? "active" : ""}`}>
          {/* Home */}
          <li
            className={`${location.pathname === "/" ? "active" : ""}`}
            onClick={handleHomeClick}
            role="button"
            tabIndex={0}
            onKeyDown={handleLogoKeyDown}
          >
            <FaHome className="nav-icon" />
            Home
          </li>

          {/* About Us */}
          <li onClick={(event) => handleDropdownToggle(event, "about")}>
            <div className="products-dropdown-title">
              <div>
                <FaInfoCircle className="nav-icon" />
                <span>About Us</span>
              </div>
              <FaChevronDown
                className={`arrow-icon ${showAbout ? "open" : ""}`}
              />
            </div>
            <ul className={`dropdown ${showAbout ? "open" : ""}`}>
              {showAbout && (
                <>
                  <li>
                    <a
                      href="#about-us"
                      onClick={(e) => {
                        e.preventDefault();
                        closeDropdowns();
                        scrollToSection("about-us");
                      }}
                    >
                      <FaInfoCircle className="drop-icon" />
                      <span>About Us</span>
                    </a>
                  </li>
                  <li>
                    <a
                      href="#executive-team"
                      onClick={(e) => {
                        e.preventDefault();
                        closeDropdowns();
                        scrollToSection("executive-team");
                      }}
                    >
                      <FaUsers className="drop-icon" />
                      <span>Leadership Team</span>
                    </a>
                  </li>
                  <li>
                    <a
                      href="#collaboration"
                      onClick={(e) => {
                        e.preventDefault();
                        closeDropdowns();
                        scrollToSection("collaboration");
                      }}
                    >
                      <FaHandshake className="drop-icon" />
                      <span>Collaboration</span>
                    </a>
                  </li>
                  <li>
                    <a
                      href="#testimonials"
                      onClick={(e) => {
                        e.preventDefault();
                        closeDropdowns();
                        scrollToSection("testimonials");
                      }}
                    >
                      <FaStar className="drop-icon" />
                      <span>Customer Reviews</span>
                    </a>
                  </li>
                  <li>
                    <a
                      href="#faq"
                      onClick={(e) => {
                        e.preventDefault();
                        closeDropdowns();
                        scrollToSection("faq");
                      }}
                    >
                      <FaBookOpen className="drop-icon" />
                      <span>FAQs</span>
                    </a>
                  </li>
                  <li>
                    <Link to="/careers" onClick={closeDropdowns}>
                      <FaBriefcase className="drop-icon" />
                      <span>Careers</span>
                    </Link>
                  </li>
                </>
              )}
            </ul>
          </li>

          {/* Products */}
          <li onClick={(event) => handleDropdownToggle(event, "products")}>
            <div className="products-dropdown-title">
              <div>
                <FaBoxOpen className="nav-icon" />
                Products
              </div>
              <FaChevronDown
                className={`arrow-icon ${showProducts ? "open" : ""}`}
              />
            </div>
            <ul className={`dropdown ${showProducts ? "open" : ""}`}>
              {showProducts && (
                <>
                  <li>
                    <Link
                      to="/products/iums-platform"
                      onClick={closeDropdowns}
                    >
                      <FaGraduationCap className="drop-icon" /> iUMS
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/products/solvarayz"
                      onClick={closeDropdowns}
                    >
                      <FaSun className="drop-icon" /> SolvaRayz
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/products/constech"
                      onClick={closeDropdowns}
                    >
                      <FaBuilding className="drop-icon" /> ConsTech
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/products/vinterio"
                      onClick={closeDropdowns}
                    >
                      <FaPalette className="drop-icon" /> vInterio
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/products/hplus"
                      onClick={closeDropdowns}
                    >
                      <FaPlusCircle className="drop-icon" /> H+
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/products/clock24"
                      onClick={closeDropdowns}
                    >
                      <FaClock className="drop-icon" /> Clock24
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/products/travelxp"
                      onClick={closeDropdowns}
                    >
                      <FaCar className="drop-icon" /> TravelXp
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/products/xpertmile"
                      onClick={closeDropdowns}
                    >
                      <FaUsers className="drop-icon" /> XpertMile
                    </Link>
                  </li>
                </>
              )}
            </ul>
          </li>

          {/* Services */}
          <li onClick={(event) => handleDropdownToggle(event, "services")}>
            <div className="products-dropdown-title">
              <div>
                <FaCog className="nav-icon" />
                Services
              </div>
              <FaChevronDown
                className={`arrow-icon ${showServices ? "open" : ""}`}
              />
            </div>
            <ul
              className={`dropdown new-dropdown ${showServices ? "open" : ""}`}
            >
              {showServices && (
                <>
                  <li>
                    <Link
                      to="/services/artificial-intelligence"
                      onClick={closeDropdowns}
                    >
                      <FaRobot className="drop-icon" /> Artificial Intelligence
                    </Link>
                  </li>
                  <li>
                    <Link to="/services/cloud-devops" onClick={closeDropdowns}>
                      <FaCloud className="drop-icon" /> Cloud & DevOps
                    </Link>
                  </li>

                  {/* <li>
                    <Link to="/services/it-consulting" onClick={closeDropdowns}>
                      <FaLightbulb className="drop-icon" /> IT Consulting
                    </Link>
                  </li> */}
                  <li>
                    <Link
                      to="/services/cyber-security"
                      onClick={closeDropdowns}
                    >
                      <FaShieldAlt className="drop-icon" /> Cyber Security
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/services/software-testing"
                      onClick={closeDropdowns}
                    >
                      <FaCheckCircle className="drop-icon" /> Software Testing
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/services/data-engineering"
                      onClick={closeDropdowns}
                    >
                      <FaDatabase className="drop-icon" /> Data Engineering
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/services/production-support"
                      onClick={closeDropdowns}
                    >
                      <FaTools className="drop-icon" /> Production Support
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/services/web-mobile-apps"
                      onClick={closeDropdowns}
                    >
                      <FaMobileAlt className="drop-icon" /> Web & Mobile Apps
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/services/software-engineering"
                      onClick={closeDropdowns}
                    >
                      <FaCog className="drop-icon" /> Software Engineering
                    </Link>
                  </li>
                  {/* <li>
                    <Link
                      to="/services/server-solutions"
                      onClick={closeDropdowns}
                    >
                      <FaServer className="drop-icon" /> Server Solutions
                    </Link>
                  </li> */}
                  <li>
                    <Link
                      to="/services/internship-training"
                      onClick={closeDropdowns}
                    >
                      <FaChalkboardTeacher className="drop-icon" /> Internship &
                      Trainings
                    </Link>
                  </li>
                </>
              )}
            </ul>
          </li>

          {/* Industries */}
          <li
            onClick={() => {
              closeDropdowns();
              scrollToSection("industries");
            }}
          >
            <FaIndustry className="nav-icon" />
            Sectors
          </li>

          {/* Contact Us */}
          <li
            onClick={() => {
              closeDropdowns();
              scrollToSection("contact");
            }}
          >
            <FaPhoneAlt className="nav-icon" />
            Contact Us
          </li>
        </ul>
      </nav>
    </>
  );
};

export default Navbar;
